import {defineStore} from 'pinia';
import API from '@/shared/helpers/API.js';
import {useUserStore} from "@/console-new/stores/user.store";
import {useNotificationStore} from "@/shared/stores";
import {DEFAULT_MAX_HOURS_ADVANCE} from "@/shared/helpers/experiences";

export const useExperiencesStore = defineStore('experiences', {
	state: () => ({
		businessExperiences: null,
		editElementsId: null,
		editScheduleId: null,
		editInfoId: null,
	}),
	getters: {
		hasExperiences: state => {
			return state.businessExperiences && state.businessExperiences.length > 0;
		}
	},
	actions: {
		getBusinessExperiences(callback = null, force = false) {
			if(this.businessExperiences != null && !force) { callback && callback(this.businessExperiences); return; }

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }
			API.init().getExperiences(userStore.currBusinessId, response => {
				if (response && response.result === "OK") {
					if(response.experiences && response.experiences.length > 0) {
						this.businessExperiences = this.normalizeExperiences(response.experiences, response.experiences_tables);
					} else {
						this.businessExperiences = [];
					}
					callback && callback(this.businessExperiences);
				}
			});
		},
		saveBusinessExperiences(callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }

			const withNew = this.businessExperiences.filter(e => e.experience_id.startsWith('newexperience'));

			API.init().saveExperiences(userStore.currBusinessId, JSON.stringify(this.businessExperiences), response => {
				const notificationStore = useNotificationStore();
				if (response && response.result === "OK") {
					if(withNew.length > 0) {
						this.getBusinessExperiences(() => {
							callback && callback(this.businessExperiences);
							notificationStore.showNotification('generic-success');
						}, true);
					} else {
						callback && callback(this.businessExperiences);
						notificationStore.showNotification('generic-success');
					}
				} else {
					notificationStore.showNotification('generic-fail');
				}
			});

		},
		saveExperienceTables(experienceId, tables, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }

			API.init().saveExperienceTables(userStore.currBusinessId, experienceId, JSON.stringify(tables), response => {
				const notificationStore = useNotificationStore();
				if (response && response.result === "OK") {
					callback && callback(tables);
					notificationStore.showNotification('generic-success');
				} else {
					notificationStore.showNotification('generic-fail');
				}
			});
		},
		saveExperienceSchedule(experienceId, timeslots, minHoursAdvance, maxHoursAdvance, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }

			API.init().saveExperienceSchedule(userStore.currBusinessId, experienceId, JSON.stringify(timeslots), minHoursAdvance, maxHoursAdvance, response => {
				useNotificationStore().showNotification(response?.result === "OK" ? 'generic-success' : 'generic-fail');
				callback && callback(response?.result === "OK");
			});
		},
		addNewExperience() {
			const nextOrderNumber = this.businessExperiences.reduce((max, e) => Math.max(max, e.order_number ?? 0), -1) + 1;
			this.businessExperiences.push({
				experience_id: 'newexperience'+Math.random(),
				order_number: nextOrderNumber,
				name: '',
				description: '',
				price: 0,
				color: '#0752A3',
				picture: null,
				slug: null,
				active: true,
				activeDropdown: true,
				info_dogs: false,
				info_children: false,
				info_special: false,
				info_allergens: false,
				min_hours_advance: null,
				max_hours_advance: DEFAULT_MAX_HOURS_ADVANCE,
				limit_people: 20,
				card_min_people: 1,
				tables: [],
				timeslots: [],
				exceptions: [],
				deleted: false
			});
		},
		reorderExperiences(orderedIds) {
			orderedIds.forEach((experienceId, position) => {
				const experience = this.businessExperiences.find(e => e.experience_id === experienceId);
				if (experience) {
					experience.order_number = position;
				}
			});
		},
		normalizeExperiences(experiences, tables) {

			let tablesMap = {};
			tables.forEach(table => {
				if(!tablesMap.hasOwnProperty(table.experience_id)) {
					tablesMap[table.experience_id] = [];
				}
				tablesMap[table.experience_id].push(table);
			});

			return experiences.map(experience => {
				experience.experience_id = experience.experience_id ? experience.experience_id+"" : null;
				experience.order_number = experience.order_number !== null && experience.order_number !== undefined ? parseInt(experience.order_number) : 0;
				experience.price = parseInt(experience.price);
				experience.active = experience.active === 1;
				experience.info_dogs = experience.info_dogs === 1;
				experience.info_children = experience.info_children === 1;
				experience.info_special = experience.info_special === 1;
				experience.info_allergens = experience.info_allergens === 1;
				experience.min_hours_advance = experience.min_hours_advance ? parseInt(experience.min_hours_advance) : null;
				experience.max_hours_advance = experience.max_hours_advance ? parseInt(experience.max_hours_advance) : null;
				experience.limit_people = experience.limit_people !== null && experience.limit_people !== undefined ? parseInt(experience.limit_people) : null;
				experience.picture = experience.picture ? experience.picture : null;
				experience.slug = experience.slug ? experience.slug : null;
				experience.deleted = false;
				experience.tables = [];
				if (tablesMap.hasOwnProperty(experience.experience_id)) {
					experience.tables = tablesMap[experience.experience_id];
				}
				experience.timeslots = [];
				experience.exceptions = [];
				return experience;
			});
		},
		normalizeTimeslot(t) {

			let startTime = t.time_start.split(":");
			if(startTime.length >= 2) {
				startTime = startTime[0] + ":" + startTime[1];
			}
			let endTime = t.time_end.split(":");
			if(endTime.length >= 2) {
				endTime = endTime[0] + ":" + endTime[1];
			}
			return {
				experience_timeslot_id: t.experience_timeslot_id ? t.experience_timeslot_id : null,
				name: t.name,
				price: t.price ? Number(t.price) : null,
				start: startTime,
				end: endTime,
				weekday: Number(t.weekday),
				time: Number(t.duration),
				intervals: Number(t.intervals),
				dontRequestCard: t.no_card === 1,
				maxPeople: t.max_people !== null && t.max_people !== undefined ? Number(t.max_people) : null,
			}
		},
		getUniqueSlug(experienceId, slug) {
			let result = slug;
			while(Object.values(this.businessExperiences).find(m => m.slug === result && m.experience_id !== experienceId)) {
				result += "-1";
			}
			return result;
		},
		getExperienceById(experienceId) {
			if(!this.businessExperiences) { return null; }
			return this.businessExperiences.find(e => Number(e.experience_id) === experienceId);
		},
	}
});
